import React, { useMemo, useCallback, useState, useEffect, forwardRef } from "react";
import { isEmpty, difference } from "lodash";
import { getBarData } from "./utils";
import { IncHorizontalBars } from "./HorizontalBar";
import { BarFilters } from "./Filters";

interface Props {
  optionsMap: Map<string, number>;
  title?: string;
  subText?: string;
  labelMap?: Record<string, string>;
  maxOptionValues?: number;
  onFilter?: (nFilterSelection: string[], currActionKeys: string[]) => void;
  selectedOptions?: string[];
  sortBySize?: boolean;
  filterComponent?: JSX.Element;
  stateLess?: boolean; // Enable this if you want the selection to be controlled externally
  considerOtherLabelAsOtherBar?: boolean;
}

export const IncHorizontalFilterBars = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    maxOptionValues = 4,
    title = "",
    subText = "",
    selectedOptions: pSelectedOptions,
    onFilter,
    optionsMap,
    labelMap,
    sortBySize = false,
    filterComponent,
    stateLess = false,
    considerOtherLabelAsOtherBar = false
  } = props;

  const [selectedOptions, setSelectedOptions] = useState<string[]>(pSelectedOptions || []);

  useEffect(() => {
    setSelectedOptions(pSelectedOptions || []);
  }, [pSelectedOptions]);

  const renderTitle = useMemo(() => !isEmpty(title), [title]);
  const renderSubText = useMemo(() => !isEmpty(subText), [subText]);
  const renderTitleArea = useMemo(() => renderTitle || renderSubText, [renderSubText, renderTitle]);

  const { displayData, filterData, idToKeysMap } = useMemo(
    () =>
      getBarData(
        optionsMap,
        maxOptionValues,
        labelMap || {},
        selectedOptions,
        sortBySize,
        considerOtherLabelAsOtherBar
      ),
    [considerOtherLabelAsOtherBar, labelMap, maxOptionValues, optionsMap, selectedOptions, sortBySize]
  );

  const onBarFilterChange = useCallback(
    (barKey: string) => {
      let nSelectedOptions = [...selectedOptions];
      const keys = idToKeysMap[barKey];
      const diff = difference(nSelectedOptions, keys);
      if (diff.length === nSelectedOptions.length) {
        nSelectedOptions = [...nSelectedOptions, ...keys];
      } else {
        nSelectedOptions = diff;
      }

      if (!stateLess) {
        setSelectedOptions(nSelectedOptions);
      }

      window.setTimeout(() => {
        if (onFilter) {
          onFilter(nSelectedOptions, keys);
        }
      });
    },
    [idToKeysMap, onFilter, selectedOptions, stateLess]
  );

  const onFilterChange = useCallback(
    (nSelectedOptions: string[]) => {
      if (!stateLess) {
        setSelectedOptions(nSelectedOptions);
      }

      window.setTimeout(() => {
        if (onFilter) {
          onFilter(nSelectedOptions, nSelectedOptions);
        }
      });
    },
    [onFilter, stateLess]
  );

  const filtersExist = displayData.length > 0;

  return (
    <div
      className="inc-horizontal-filter-bars"
      ref={ref}
    >
      {renderTitleArea && (
        <div className="inc-horizontal-filter-bars--title-area">
          {renderTitle && <div className="inc-horizontal-filter-bars--title-area__title">{title}</div>}
          {renderSubText && <div className="inc-horizontal-filter-bars--title-area__subtitle marginLt6">{subText}</div>}
        </div>
      )}
      <div className="inc-horizontal-filter-bars--bars-area">
        {filtersExist ? (
          <IncHorizontalBars
            colors="gradient"
            data={displayData}
            onClick={onBarFilterChange}
          />
        ) : (
          <div className="inc-horizontal-filter-bars--bars-area__no-filters">No filters</div>
        )}
      </div>
      {filtersExist && (
        <div className="inc-horizontal-filter-bars--filters-area">
          {filterComponent ? (
            filterComponent
          ) : filterData.length > maxOptionValues ? (
            <BarFilters
              filterData={filterData}
              label={title}
              onFilter={onFilterChange}
              selectedValues={selectedOptions}
            />
          ) : null}
        </div>
      )}
    </div>
  );
});
